export type FilterValue = string | boolean | null

export type Filters<F = unknown> = Record<keyof F, FilterValue>

export type ListSearch = Record<string, string | number | boolean | undefined>

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined
}

const RESERVED = new Set(['q', 'sort', 'page', 'size'])

const PAGE_SIZES = new Set([10, 25, 50, 100])

function readFilter(value: unknown): string | boolean | undefined {
  if (typeof value === 'boolean') return value
  if (typeof value === 'string' && value !== '') return value
  return undefined
}

export function validateListSearch(input: Record<string, unknown>): ListSearch {
  const out: ListSearch = {}

  const q = readString(input['q'])
  if (q !== undefined) out['q'] = q

  const sort = readString(input['sort'])
  if (sort !== undefined) out['sort'] = sort

  const page = Number(input['page'])
  if (Number.isInteger(page) && page > 1) out['page'] = page

  const size = Number(input['size'])
  if (PAGE_SIZES.has(size)) out['size'] = size

  for (const [key, value] of Object.entries(input)) {
    if (RESERVED.has(key)) continue
    const filter = readFilter(value)
    if (filter !== undefined) out[key] = filter
  }

  return out
}

export function readBoolean(value: unknown): boolean {
  return value === true || value === '1' || value === 'true'
}

export function filtersToSearch<F extends Filters<F>>(filters: F, defaults: F): ListSearch {
  const out: ListSearch = {}
  for (const key of Object.keys(filters) as (keyof F)[]) {
    const value = filters[key]
    if (value === defaults[key]) continue
    if (typeof value === 'boolean' || typeof value === 'string') out[String(key)] = value
  }
  return out
}

export function searchToFilters<F extends Filters<F>>(search: ListSearch, defaults: F): F {
  const out = { ...defaults }
  for (const key of Object.keys(defaults) as (keyof F)[]) {
    const raw = search[String(key)]
    if (raw === undefined) continue
    if (typeof defaults[key] === 'boolean') {
      out[key] = readBoolean(raw) as F[keyof F]
    } else if (typeof raw === 'string') {
      out[key] = raw as F[keyof F]
    }
  }
  return out
}
